/**
 * WordCardsKo — the KR money page at /ko/word-cards (landing-v12 P1-③).
 *
 * Persona-2's commercial-intent surface: `단어장 만들기 사이트` /
 * `영단어 암기 사이트` families (260/mo, KD 9-18) rank TOOL HOMEpages in
 * KR Google — a product page competes there, a blog post doesn't. ko-only:
 * top-level absolute route in App.tsx, NOT inside PublicRoutes' double
 * mount (that would clone the page under /, /zh-TW/, /ja/), copy is
 * component-local (no 8-locale parity surface). CTA = 앱 열기 → /login —
 * list-bringers don't install an extension.
 *
 * Honesty gates (landing-v11 §2, ko copy owner-reviewed before deploy):
 * paste/PDF → proposal checklist (≤20 cards/confirm) but NO score claim
 * (rankCues runs on the media route only); upload = English audio, 90-min
 * cap, 0-100 score scoped here alone; FSRS-6 described as "Anki가 채택한
 * 최신 스케줄러" (allowed framing); 150 energy ≈ 150 cards, never
 * "hundreds"; no 무제한.
 */

import { useEffect } from 'react'
import { ArrowRight, ClipboardPaste, FileAudio, FileText, Zap, CalendarClock, CloudOff } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { Footer } from './Footer'
import { LandingI18nProvider } from './LandingI18n'
import { track } from '../../lib/betterstack'

const PATHS = [
  {
    icon: ClipboardPaste,
    title: '텍스트 붙여넣기',
    desc: '교재 한 페이지, 손에 있는 단어장, 시험 범위 목록. 붙여넣으면 카드 제안이 돌아옵니다 — 전체 선택도 가능하고, 걸러 남기기도 됩니다. 한 번의 확인으로 최대 20장.',
  },
  {
    icon: FileText,
    title: 'PDF 올리기',
    desc: '강의 자료, 해커스 보카 같은 교재 단어장, 받아둔 pdf — 같은 제안 흐름을 통과합니다. 카드가 놓이면 고르고, 확인 한 번으로 끝.',
  },
  {
    icon: FileAudio,
    title: '강의 녹음 올리기',
    desc: '영어 강의나 팟캐스트 녹음(최대 90분)은 전사된 뒤 구문마다 배울 가치 점수(0–100, 학습 목표 반영)가 붙고, 남길 것들은 미리 체크되어 돌아옵니다. 영어 학습은 영어 오디오를 올립니다.',
  },
]

const VS_ANKI = [
  {
    icon: Zap,
    title: '설치도 설정도 없음',
    desc: '브라우저에서 바로 열리고, 카드 레이아웃이나 복습 설정을 만질 게 없습니다. 재료를 주는 일만 하면 됩니다.',
  },
  {
    icon: CalendarClock,
    title: 'FSRS-6가 일정을 잡음',
    desc: 'Anki가 채택한 최신 스케줄러를 기본으로 돌립니다. 카드마다 잊기 직전의 날에 돌아오고, 180일 살아남으면 졸업합니다.',
  },
  {
    icon: CloudOff,
    title: '덱 검색 대신 내 재료',
    desc: '남이 만든 덱을 찾아 헤매는 대신, 이번 시험 범위와 내 교재가 그대로 덱이 됩니다. 카드는 클라우드에 저장되어 어느 기기에서나 이어집니다.',
  },
]

const FAQ = [
  {
    q: '무료로 시작할 수 있나요?',
    a: '네. 시작할 때 150 에너지가 주어지고 카드 한 장에 1 에너지입니다 — 약 150장을 만들어 보고 나서도 월 요금은 없습니다. 더 쓰고 싶을 때만 $5/$10/$20 충전하고, 충전한 에너지는 만료되지 않습니다.',
  },
  {
    q: '받아둔 단어장 pdf도 되나요?',
    a: '됩니다. pdf를 올리면 카드 제안이 나옵니다. 이미 아는 단어는 걸러 내고, 남는 것만 카드로 만들어 복습으로 돌리는 흐름이 기본입니다.',
  },
  {
    q: '스마트폰에서도 쓸 수 있나요?',
    a: '네 — 설치가 필요 없는 웹 앱이라 브라우저가 있으면 되고, 카드는 서버에 저장되어 기기를 바꿔도 이어집니다.',
  },
]

export function WordCardsKo() {
  return (
    <LandingI18nProvider>
      <WordCardsKoBody />
    </LandingI18nProvider>
  )
}

function WordCardsKoBody() {
  const revealRef = useScrollReveal()

  useEffect(() => {
    document.title = '단어장 만들기 사이트 — 교재·PDF가 FSRS 카드 덱이 됩니다 | Piccard'
  }, [])

  const cta = (source: string) => (
    <a
      href={`/login?source=${source}`}
      onClick={() => track('money-cta-click', { page: 'ko-word-cards', source })}
      className="inline-flex items-center px-7 py-3.5 rounded-full bg-primary text-on-primary font-semibold text-base shadow-card hover:bg-primary-active transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
    >
      앱 열기
      <ArrowRight className="ml-2 w-5 h-5" />
    </a>
  )

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="border-b border-outline-variant">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/ko/" className="font-headline font-extrabold text-lg text-primary">
            Piccard
          </a>
          <a href="/ko/" className="text-sm text-muted-soft hover:text-on-surface transition-colors">
            홈
          </a>
        </div>
      </header>

      {/* The reveal container must wrap EVERY section's .reveal elements — it
          sat on the hero div only on first ship, so every section below the
          fold stayed opacity-0 forever (blank-sections bug, 2026-09-08). */}
      <main ref={revealRef} className="flex-1">
        {/* Hero */}
        <section className="py-16 md:py-24 bg-surface-container-low">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="reveal font-headline font-extrabold text-3xl sm:text-5xl text-on-surface text-balance">
              단어장 만들기 — 교재 한 장이 FSRS 카드 덱이 됩니다
            </h1>
            <p className="reveal mt-5 text-lg text-on-surface-variant leading-relaxed">
              손에 있는 단어장·pdf·강의 녹음을 붙여넣거나 올리면, Piccard가 카드를 제안하고{' '}
              FSRS-6가 복습 일정을 잡습니다. 설치도 설정도 없이, 브라우저에서 바로.
            </p>
            <div className="reveal mt-8 flex flex-col items-center gap-3">
              {cta('kr_money_hero')}
              <p className="text-muted-soft text-xs">시작 에너지 150장 무료 · 신용카드 없이 시작</p>
            </div>
          </div>
        </section>

        {/* Three paths */}
        <section className="py-16 md:py-24 bg-surface">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-4 text-balance">
              재료는 이미 손에 있습니다
            </h2>
            <p className="reveal text-on-surface-variant text-lg text-center mb-12 max-w-2xl mx-auto">
              무엇을 올리느냐의 차이일 뿐, 흐름은 하나입니다 — 제안된 카드에서 배울 것을 고르고,
              나머지는 복습 일정이 알아서 가져옵니다.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PATHS.map((p, i) => (
                <div
                  key={p.title}
                  className={`reveal reveal-stagger-${i + 1} hover-lift rounded-2xl bg-surface-container-low p-6 sm:p-8`}
                >
                  <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-surface-card text-primary">
                    <p.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-headline font-bold text-lg mb-2 text-on-surface">{p.title}</h3>
                  <p className="text-sm leading-relaxed text-on-surface-variant">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* vs Anki */}
        <section className="py-16 md:py-24 bg-surface-container-low">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-4 text-balance">
              왜 Anki가 아니라 Piccard인가
            </h2>
            <p className="reveal text-on-surface-variant text-lg text-center mb-12 max-w-2xl mx-auto">
              Anki는 강력하지만, 카드를 직접 만들고 설정을 손봐야 유지됩니다. Piccard는 조립 쪽을
              지웁니다.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {VS_ANKI.map((v, i) => (
                <div
                  key={v.title}
                  className={`reveal reveal-stagger-${i + 1} hover-lift rounded-2xl bg-surface p-6 sm:p-8`}
                >
                  <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-surface-card text-primary">
                    <v.icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-headline font-bold text-lg mb-2 text-on-surface">{v.title}</h3>
                  <p className="text-sm leading-relaxed text-on-surface-variant">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing strip */}
        <section className="py-16 md:py-24 bg-surface">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface mb-4">
              쓴 만큼만 내는 요금
            </h2>
            <p className="reveal text-on-surface-variant text-lg leading-relaxed">
              카드 한 장에 1 에너지. 시작하면 150 에너지(약 150장)가 무료로 주어지고, 더 필요할 때
              $5/$10/$20로 500·1,200·2,700 에너지를 충전합니다. 월 요금은 없고, 충전한 에너지는 만료되지
              않습니다.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-surface-container-low">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-10">
              자주 묻는 질문
            </h2>
            <div className="grid gap-4">
              {FAQ.map((f) => (
                <details key={f.q} className="reveal group rounded-2xl bg-surface px-6 py-4">
                  <summary className="cursor-pointer list-none font-semibold text-sm text-on-surface flex items-center justify-between">
                    {f.q}
                    <span className="text-muted-soft transition-transform group-open:rotate-180">▾</span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-on-surface-variant">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gradient-to-br from-primary to-[#4a1828] py-16 md:py-24">
          <div className="reveal max-w-3xl mx-auto px-4 text-center sm:px-6">
            <h2 className="font-headline font-bold text-3xl sm:text-4xl text-on-primary text-balance">
              오늘 받아둔 목록부터 올려보세요
            </h2>
            <p className="mt-4 text-on-primary/80 text-lg">
              첫 150장은 무료입니다. 복습 일정은 Piccard가 가져갑니다.
            </p>
            <div className="mt-8 flex justify-center">
              <a
                href="/login?source=kr_money_final"
                onClick={() => track('money-cta-click', { page: 'ko-word-cards', source: 'kr_money_final' })}
                className="inline-flex items-center px-7 py-3.5 rounded-full bg-surface text-primary font-semibold text-base shadow-card hover:opacity-95 transition-opacity"
              >
                앱 열기
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
